import { Injectable } from '@angular/core';
import dayjs, { Dayjs, ManipulateType } from 'dayjs';
import duration from 'dayjs/plugin/duration';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import localeData from 'dayjs/plugin/localeData';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import { Locale } from 'dayjs/locale/*';

dayjs.extend(duration);
dayjs.extend(customParseFormat);
dayjs.extend(localeData);
dayjs.extend(localizedFormat);

@Injectable({
    providedIn: 'root'
})
export class DateUtilsService {
    static readonly YEAR_DATE_MONTH = 'YYYY-MM-DD';
    static readonly DAY_MONTH_YEAR = 'DD.MM.YYYY';
    static readonly HOUR_MINUTES = 'HH:mm';
    static readonly DATE_AND_TIME = 'DD.MM.YYYY HH:mm';

    setLocale(locale: Locale): void {
        dayjs.locale(locale);
    }

    convertToSeconds(period: string): number {
        const values = period.replace('d', '').replace('h', '').replace('m', '').split(' ');

        const duration = dayjs.duration({
            minutes: Number(values[2]),
            hours: Number(values[1]),
            days: Number(values[0])
        });
        return duration.asSeconds();
    }

    convertToDaysHoursSeconds(seconds: number): string {
        return dayjs.duration(seconds, 'seconds').format('D[d] H[h] M[m]');
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    convertToIsoFromDates(date: string | Date, time: any): string {
        const transformedDate: Date = this.convertDateOfDifferentForms(date);
        if (!dayjs(time).isValid()) {
            const timeArray: string[] = time.split(':');
            return this.setHoursAndMinutes(transformedDate, +timeArray[0], +timeArray[1]).toISOString();
        }
        return this.setHoursAndMinutes(transformedDate, time.getHours(), time.getMinutes()).toISOString();
    }

    convertDateToIso(date: Date): string {
        return date.toISOString();
    }

    formatDate(date?: string, format?: string): string {
        if (date && format) {
            return dayjs(date).format(format);
        } else {
            return '';
        }
    }

    getDay(date: Date): number {
        return dayjs(date).day();
    }

    getWeekdays(): string[] {
        return dayjs.localeData().weekdaysMin();
    }

    getFirstDayOfWeek(): number {
        return dayjs.localeData().firstDayOfWeek();
    }

    isInPastWithoutTime(date: Date): boolean {
        const date1 = dayjs(date);
        const date2: Dayjs = dayjs().set('hours', 0).set('minutes', 0).set('seconds', 0).set('milliseconds', 0);
        return date1.isBefore(date2);
    }

    isInPast(date: string | Date | undefined): boolean {
        return !dayjs().isBefore(date);
    }

    convertToDate(date: Date | string, format?: string): Date {
        if (format) {
            return dayjs(date, format).toDate();
        } else {
            return dayjs(date).toDate();
        }
    }

    convertStringToISO(date: string): string {
        return dayjs(date).toISOString();
    }

    getDayName(dateStr: string): string {
        const date: Date = this.convertToDate(dateStr);
        // TODO: hier muss noch die atkuelle local eingebunden werden
        return date.toLocaleDateString('de-DE', { weekday: 'long' });
    }

    addTimeToDate(dateStr: string, duration: number, type: ManipulateType | undefined): string {
        return dayjs(dateStr).add(duration, type).toISOString();
    }

    differenceBetweenDates(firstDate: string, secondDate: string): number {
        const date1 = dayjs(firstDate);
        const date2 = dayjs(secondDate);
        return date2.diff(date1, 's');
    }

    private convertDateOfDifferentForms(date: string | Date): Date {
        if (typeof date === 'string') {
            if (this.checkDateFormat(date)) {
                return this.convertToDate(date, 'DD/MM/YYYY');
            } else if (date.includes('.')) {
                return this.convertToDate(date, 'DD.MM.YYYY');
            } else {
                return dayjs(date).toDate();
            }
        } else {
            return date;
        }
    }

    private setHoursAndMinutes(date: Date, hours: number, minutes: number): Date {
        date.setHours(hours);
        date.setMinutes(minutes);
        return date;
    }

    private checkDateFormat(date: string): boolean {
        return date.includes('/');
    }
}
